import axios from 'axios';

/**
 * Frontend Component Settings Helper Utility
 * Generic on/off feature toggles per institute (e.g. "Download Report" button visibility),
 * managed the same way as OTP settings but keyed by a settings name other than OTP.
 */

let settingsCache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (componentName, settingName) => `${settingName}:${componentName}`;

/**
 * Check if a given setting is enabled for a component
 *
 * @param {string} componentName - Name of the component (e.g., 'StudentManagement')
 * @param {string} settingName - Name of the setting (e.g., 'Download Report')
 * @param {boolean} bypassCache - Force fetch from server ignoring cache (default: false)
 * @returns {Promise<boolean>} - true if enabled, false otherwise
 */
export const isComponentSettingEnabled = async (componentName, settingName, bypassCache = false) => {
  const cacheKey = getCacheKey(componentName, settingName);
  try {
    if (!bypassCache && settingsCache[cacheKey]) {
      const cached = settingsCache[cacheKey];
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.isEnabled;
      }
    }

    const instituteId = localStorage.getItem('instituteId');
    const params = { setting_name: settingName };
    if (instituteId) {
      params.institute_id = instituteId;
    }

    const response = await axios.get(`/api/component-settings/${componentName}`, {
      params,
      withCredentials: true
    });
    const isEnabled = response.data.is_enabled ?? true;

    settingsCache[cacheKey] = { isEnabled, timestamp: Date.now() };
    return isEnabled;
  } catch (error) {
    console.error(`Error checking "${settingName}" status for ${componentName}:`, error);
    return true;
  }
};

/**
 * Clear the component settings cache
 *
 * @param {string} componentName - Specific component to clear cache for (optional)
 * @param {string} settingName - Specific setting to clear cache for (optional, requires componentName)
 */
export const clearComponentSettingsCache = (componentName = null, settingName = null) => {
  if (componentName && settingName) {
    delete settingsCache[getCacheKey(componentName, settingName)];
  } else {
    settingsCache = {};
  }
};

export default {
  isComponentSettingEnabled,
  clearComponentSettingsCache
};
